import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import { cn } from '@crowdex/ui/lib/utils';
import { ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import type { PluginWorkbench } from '@/features/plugins/plugin-workbench';
import { artifactGlyph, hasPreview, LibraryCard } from './library-card';
import type { PluginGroup } from './library-model';

/**
 * The grid of the Library's cards, pulled out by their padding: their pictures line up with the
 * section titles.
 */
export const LIBRARY_GRID =
  'grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-3 -mx-2 max-sm:mx-0 max-sm:grid-cols-2 max-sm:gap-x-3 max-sm:gap-y-4';

/** A section of the Library: its label (the charter's grey section label), then its content. */
export function LibrarySection({
  title,
  count,
  children,
  className,
}: {
  title: string;
  count?: number;
  children: ReactNode;
  className?: string;
}) {
  const id = `library-section-${title.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`;
  return (
    <section aria-labelledby={id} className={className}>
      <h2 className="type-section mb-3 flex items-baseline gap-2 text-muted-foreground" id={id}>
        {title}
        {count !== undefined && <span className="text-subtle-foreground">{count}</span>}
      </h2>
      {children}
    </section>
  );
}

/**
 * The cards of what plugins provide: each opens over the page, "Use" starts from it; or, on an
 * app's home, picking one starts from it (`onPick`, its templates).
 */
export function PluginCards({
  label,
  artifacts,
  workbench,
  using,
  ...actions
}: {
  label: string;
  artifacts: readonly ArtifactListingEntry[];
  workbench: PluginWorkbench;
  using: string | null;
} & (
  | { onOpen(artifact: ArtifactListingEntry): void; onUse(artifact: ArtifactListingEntry): void }
  | { onPick(artifact: ArtifactListingEntry): void }
)) {
  return (
    <ul aria-label={label} className={LIBRARY_GRID}>
      {artifacts.map((artifact) => (
        <LibraryCard
          artifact={artifact}
          glyph={artifactGlyph(artifact, workbench)}
          key={artifact.id}
          preview={hasPreview(artifact, workbench)}
          provided
          using={using === artifact.id}
          {...('onPick' in actions
            ? { onOpen: actions.onPick }
            : { onOpen: actions.onOpen, onUse: actions.onUse })}
        />
      ))}
    </ul>
  );
}

/**
 * "From your plugins": what each app provides (its templates, its design systems), one group per
 * app, collapsed with its count unless `open` (a search or a kind chosen shows what matches).
 */
export function PluginGroups({
  groups,
  open,
  workbench,
  using,
  onOpen,
  onUse,
}: {
  groups: readonly PluginGroup[];
  open: boolean;
  workbench: PluginWorkbench;
  using: string | null;
  onOpen(artifact: ArtifactListingEntry): void;
  onUse(artifact: ArtifactListingEntry): void;
}) {
  // What the user opened or closed, by app; the others follow `open`.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  return (
    <div className="flex flex-col gap-2">
      {groups.map((group) => {
        const expanded = toggled[group.plugin] ?? open;
        const glyph = artifactGlyph(
          { plugin: group.plugin, type: group.artifacts[0]?.type ?? '' },
          workbench,
        );
        const listId = `library-plugin-${group.plugin.replaceAll(/[^a-zA-Z0-9]+/g, '-')}`;
        return (
          <div
            className="rounded-2xl border bg-card max-sm:border-0 max-sm:bg-transparent"
            key={group.plugin}
          >
            <button
              aria-controls={listId}
              aria-expanded={expanded}
              className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:px-0"
              data-library-plugin-group={group.plugin}
              onClick={() => setToggled((current) => ({ ...current, [group.plugin]: !expanded }))}
              type="button"
            >
              <ChevronRight
                aria-hidden
                className={cn(
                  'size-4 shrink-0 text-muted-foreground transition-transform duration-ui',
                  expanded && 'rotate-90',
                )}
              />
              <span
                aria-hidden
                className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-control text-muted-foreground"
              >
                <ItemIcon className="size-3.5" icon={glyph.icon} src={glyph.src} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2">
                  <span className="truncate font-medium text-sm">{group.title}</span>
                  <span className="type-caption text-subtle-foreground">
                    {group.artifacts.length}
                  </span>
                </span>
                <span className="type-caption block truncate text-muted-foreground">
                  {group.kinds.map((kind) => `${kind.label} ${kind.count}`).join(' · ')}
                </span>
              </span>
            </button>
            {expanded && (
              <div className="px-2 pt-1 pb-2 max-sm:px-0" id={listId}>
                <PluginCards
                  artifacts={group.artifacts}
                  label={`${group.title} · from your plugins`}
                  onOpen={onOpen}
                  onUse={onUse}
                  using={using}
                  workbench={workbench}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
